import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { ShieldCheck } from 'lucide-react'
import type { ReactNode } from 'react'
import { QueryState } from '~/components/new/app-ui/query-state'
import type { AdminMfaStatus } from '~/components/routes/account-security/account-security'
import { MfaEnrollment } from '~/components/routes/account-security/mfa-enrollment'
import {
  MfaStatusBadge,
  MfaStatusBadgeSkeleton,
} from '~/components/routes/account-security/mfa-status-badge'
import {
  adminMfaStatusQueryOptions,
  hasAdminSession,
} from '~/components/routes/account-security/mfa-status-query'
import { Alert, AlertDescription } from '~/components/ui/alert'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card'
import { Bone } from '~/components/ui/skeleton'
import { frenchParisDayOfInstant } from '~/lib/iso-date'

/**
 * `/compte/securite` — « Sécurité du compte » (S1.4b) : le double facteur de l'admin connecté.
 * Son propre compte seulement, sans droit requis.
 */
export const Route = createFileRoute('/_authenticated/compte/securite')({
  component: AccountSecurityPage,
})

/** Carte du double facteur ; `badge` et le contenu changent avec l'état, le cadre reste. */
const MfaCard = ({ badge, children }: { badge: ReactNode; children: ReactNode }) => (
  <Card>
    <CardHeader>
      <div className="flex items-center justify-between gap-3">
        <CardTitle>Double facteur</CardTitle>
        {badge}
      </div>
      <CardDescription>
        En plus du mot de passe, un code à six chiffres de votre application d'authentification est
        demandé à chaque connexion.
      </CardDescription>
    </CardHeader>
    <CardContent className="flex flex-col gap-4">{children}</CardContent>
  </Card>
)

/** Chargement : la carte, son badge et le bouton d'activation, sans l'état. */
const MfaCardSkeleton = () => (
  <MfaCard badge={<MfaStatusBadgeSkeleton />}>
    <p className="text-sm">
      <Bone width="40ch" />
    </p>
    <Button type="button" className="w-fit" disabled>
      <ShieldCheck className="size-4" />
      Activer le double facteur
    </Button>
  </MfaCard>
)

const MfaCardContent = ({ status }: { status: AdminMfaStatus }) => {
  if (status.enrolled) {
    return (
      <MfaCard badge={<MfaStatusBadge enrolled />}>
        <p className="text-sm">
          Activé{status.enrolledAt ? ` le ${frenchParisDayOfInstant(status.enrolledAt)}` : ''}.
          Téléphone perdu : connectez-vous avec un code de secours, puis demandez à un
          administrateur de réinitialiser votre double facteur pour l'activer sur le nouveau.
        </p>
      </MfaCard>
    )
  }

  const { requiredFrom, applies } = status.obligation
  return (
    <MfaCard badge={<MfaStatusBadge enrolled={false} />}>
      {applies && requiredFrom && (
        <Alert variant="warning">
          <AlertDescription className="text-warning">
            Obligatoire pour votre compte à partir du {frenchParisDayOfInstant(requiredFrom)} : il
            donne accès à d'autres comptes, aux rôles ou à la RH.
          </AlertDescription>
        </Alert>
      )}
      <MfaEnrollment />
    </MfaCard>
  )
}

function AccountSecurityPage() {
  const query = useQuery(adminMfaStatusQueryOptions())
  // Sans jeton (accès direct, Retour après déconnexion), la requête ne part pas : aucun 401 ne
  // ramènerait à la connexion, le squelette resterait affiché.
  if (!hasAdminSession()) return <Navigate to="/login" replace />

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6 p-6">
      <header className="flex items-center gap-3">
        <span className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <ShieldCheck className="size-5" />
        </span>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Sécurité du compte</h1>
          <p className="text-sm text-muted-foreground">Connexion de votre compte au back-office</p>
        </div>
      </header>

      <QueryState
        query={query}
        skeleton={<MfaCardSkeleton />}
        errorFallback="Impossible de charger l'état du double facteur."
      >
        {(status) => <MfaCardContent status={status} />}
      </QueryState>
    </div>
  )
}
